import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Loader2, Printer, Save } from "lucide-react";

/** THE DEALER ASK SHEET — one page he can hand or email a dealer's counter.
 *
 *  The whole point of it: a build record PRINTED to PDF reads perfectly and costs nothing, while the
 *  same record photocopied is only a picture that has to be read by eye. So the sheet asks, plainly,
 *  for "Print → Save as PDF". His own details are typed once here and remembered, so the sheet never
 *  carries a made-up address or a made-up phone number. */

const FIELDS = [
  ["business_name", "Your business name", "Friedrich's Auto & Truck"],
  ["contact_name", "Who they should ask for", "e.g. Ryan"],
  ["telephone", "Telephone", "e.g. (000) 000-0000"],
  ["email", "Email for the PDF", "e.g. parts@…"],
  ["address", "Address", "street, town"],
  ["account_number", "Your account # with them (if any)", "optional"],
];

export function DealerAskPanel({ canManage = false, testid = "dealer-ask" }) {
  const [s, setS] = useState({});
  const [vins, setVins] = useState("");
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dealer-ask/settings");
      setS(data.settings || {});
    } catch { /* the sheet still prints with blank lines to write on */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put("/dealer-ask/settings", s);
      setS(data.settings || {});
      setSaved(true);
      toast.success(data.say || "Saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save that.");
    } finally { setBusy(""); }
  };

  const open = async (params) => {
    setBusy("open");
    try {
      const { data } = await api.get("/dealer-ask", { params, responseType: "text" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/html" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open the sheet.");
    } finally { setBusy(""); }
  };

  return (
    <section data-testid={testid} className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-lg font-bold truncate">The dealer ask sheet</h2>
        </div>
        <span data-testid={`${testid}-state`}
          className="text-[10px] font-mono uppercase tracking-widest bg-emerald-600 px-2 py-1 rounded shrink-0">
          Free
        </span>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-sm text-slate-700">
          One page to hand or email a dealer's counter. It asks them to
          <b> print the build record straight to PDF, not scan it</b> — a printed PDF reads perfectly
          and costs nothing, a photocopy is only a picture and has to be read by eye.
        </p>

        <div className="grid sm:grid-cols-2 gap-2">
          {FIELDS.map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
              <Input data-testid={`${testid}-${k}`} value={s[k] || ""} placeholder={ph}
                disabled={!canManage}
                onChange={(e) => { setSaved(false); setS((p) => ({ ...p, [k]: e.target.value })); }}
                className="rounded-lg h-11 mt-0.5" />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          Typed once and remembered. Anything left empty prints as a line to write on — never a
          made-up detail.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button data-testid={`${testid}-save`} onClick={save} disabled={!canManage || busy === "save"}
            variant="outline" className="rounded-lg h-12 px-4 border-slate-300 font-bold">
            {busy === "save" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {saved ? "Saved" : "Save my details"}
          </Button>
          <Button data-testid={`${testid}-blank`} onClick={() => open({})} disabled={busy === "open"}
            className="rounded-lg h-12 px-4 bg-[#1F1F1F] hover:bg-black font-bold">
            <Printer className="w-4 h-4 mr-1.5" />Open a blank one to print
          </Button>
        </div>

        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
            Or for particular trucks — paste VINs or stock numbers
          </span>
          <Input data-testid={`${testid}-vins`} value={vins} placeholder="1XPWD40X1ED215307, 1FUJA6CK…"
            onChange={(e) => setVins(e.target.value)} className="rounded-lg h-11 font-mono text-sm" />
          <Button data-testid={`${testid}-for-vins`} onClick={() => open({ vins })}
            disabled={!vins.trim() || busy === "open"} variant="outline"
            className="rounded-lg h-12 px-4 border-slate-300 font-bold">
            <Printer className="w-4 h-4 mr-1.5" />Print the ask for these trucks
          </Button>
          <p className="text-[11px] text-slate-500">
            The year, make, model and your stock number are filled in from your own records. A VIN it
            does not know still goes on the sheet, as you typed it.
          </p>
        </div>
      </div>
    </section>
  );
}

export default DealerAskPanel;
